import GroupOverview from "./pages/Groups/GroupOverview";
import AnalyticsPage, { LegacyWorkbooksRedirect } from './pages/Analytics/AnalyticsPage';
import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import DashboardLayout from "./layouts/DashboardLayout";
import { ResetPassword } from "./pages/Auth";
import ProtectedRoute from "./components/ProtectedRoute";
import Home from "./pages/Home/Home";

import NotFound from "./pages/NotFound";
import Unauthorized from "./pages/Unauthorized";
import OrganisationDetails from "./pages/Organisation/OrganisationDetails";
import Branding from "./pages/Organisation/Branding";
import PlaySignals from "./pages/PlaySignals";
import { useUserProfile } from "./context/UserProfileContext";
import SuperAdminShellNotice from "./pages/SuperAdminShellNotice";
import { EnvironmentBanner } from "./components/EnvironmentBanner";
import HandoffHandler from "./components/HandoffHandler";
import TrialExpired from "./pages/TrialExpired";
import { installTrialExpiredHandler } from "./utils/trialExpiredHandler";

installTrialExpiredHandler();

// App Launchpad URL - profile management is centralized there
const APP_HOME_URL = process.env.REACT_APP_APP_HOME_URL || "https://app.pedagogue.io";

const ExternalProfileRedirect: React.FC = () => {
  React.useEffect(() => {
    window.location.replace(`${APP_HOME_URL}/profile`);
  }, []);
  return null;
};

const ExternalLoginRedirect: React.FC = () => {
  React.useEffect(() => {
    const params = window.location.search;
    window.location.replace(`${APP_HOME_URL}/login${params}`);
  }, []);
  return null;
};

const HomeOrRedirect: React.FC = () => {
  const { userProfile } = useUserProfile();

  if (userProfile?.role === "super_admin" && !userProfile?.organisation_id) {
    return <Navigate to="/super-admin" replace />;
  }

  return <Home />;
};

const App: React.FC = () => {
  const environmentLabel = process.env.REACT_APP_ENVIRONMENT_LABEL;

  return (
    <Router>
      <HandoffHandler>
        <EnvironmentBanner label={environmentLabel} />
        <Routes>
        {/* Login decommissioned — redirect to App Launchpad login */}
        <Route path="/login" element={<ExternalLoginRedirect />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/unauthorized" element={<Unauthorized />} />
        <Route path="/workbooks/:organisationId" element={<ProtectedRoute requireOrganization={true} allowedRoles={["admin","super_admin"]}><LegacyWorkbooksRedirect/></ProtectedRoute>} />
        <Route path="/trial-expired" element={<TrialExpired />} />
        <Route
          path="/"
          element={
            <ProtectedRoute requireOrganization={true} allowedRoles={["admin", "super_admin"]}>
              <DashboardLayout>
                <HomeOrRedirect />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/home"
          element={
            <ProtectedRoute requireOrganization={true} allowedRoles={["admin", "super_admin"]}>
              <DashboardLayout>
                <HomeOrRedirect />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/organisation/:organisationId"
          element={
            <ProtectedRoute requireOrganization={true} allowedRoles={["admin", "super_admin"]}>
              <DashboardLayout>
                <OrganisationDetails />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/branding/:organisationId"
          element={
            <ProtectedRoute requireOrganization={true} allowedRoles={["admin", "super_admin"]}>
              <DashboardLayout>
                <Branding />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route path="/groups/:groupId/overview" element={<ProtectedRoute requireOrganization={true} allowedRoles={["admin", "super_admin"]}><DashboardLayout><GroupOverview /></DashboardLayout></ProtectedRoute>} />
        <Route
          path="/analytics/dashboard/:organizationId"
          element={
            <ProtectedRoute requireOrganization={true} allowedRoles={["admin", "super_admin"]}>
              <DashboardLayout>
                <AnalyticsPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        {/* Home-org-only by design: the backend /admin/play/* APIs take no
            org parameter and always scope to the viewer's organisation. */}
        <Route
          path="/play"
          element={
            <ProtectedRoute requireOrganization={true} allowedRoles={["admin", "super_admin"]}>
              <DashboardLayout>
                <PlaySignals />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />


        {/* Profile centralized in App Launchpad — redirect for backward compat */}
        <Route path="/profile" element={<ExternalProfileRedirect />} />
        <Route
          path="/super-admin"
          element={
            <ProtectedRoute requireOrganization={false} allowedRoles={["super_admin"]}>
              <DashboardLayout>
                <SuperAdminShellNotice />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route path="/super-admin/*" element={<Navigate to="/super-admin" replace />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      </HandoffHandler>
    </Router>
  );
};

export default App;
